
import React from "react";
import { Button } from "@/components/ui/button";
import { Clock, Heart, Lock, Mail, Gift } from "lucide-react";

const ConceptItem = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <div className="flex items-center space-x-3 mb-4">
    <div className="bg-legatia-orange/10 p-2 rounded-full">
      {icon}
    </div>
    <p className="text-gray-700">{text}</p>
  </div>
);

const NewConcept = () => {
  return (
    <section id="concept" className="section-padding bg-legatia-yellow/10 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-10 left-0 w-72 h-72 bg-legatia-orange/5 rounded-full"></div>
      <div className="absolute bottom-10 right-0 w-96 h-96 bg-legatia-yellow/10 rounded-full"></div>
      <div className="absolute top-1/3 left-1/4 w-8 h-8 bg-legatia-green/20 rounded-full"></div>
      <div className="absolute bottom-1/3 right-1/4 w-12 h-12 bg-legatia-orange/20 rounded-full"></div>
      
      <div className="container mx-auto relative z-10">
        <h2 className="section-title text-center">
          L'essence de <span className="gradient-text">Legatia</span>
        </h2>
        <p className="section-subtitle text-center max-w-4xl mx-auto">
          Un pont émotionnel entre aujourd'hui et les moments qui comptent demain.
        </p>
        
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12 relative">
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-legatia-orange/20 to-legatia-yellow/20 rounded-bl-3xl -mt-6 -mr-6 z-0"></div>
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row gap-8 mb-8">
              <div className="md:w-1/3 flex justify-center">
                <div className="relative">
                  <div className="w-full aspect-square max-w-xs bg-legatia-yellow/30 rounded-full transform -rotate-6 absolute"></div>
                  <img 
                    src="/assets/memory-box.jpg" 
                    alt="Boîte à souvenirs" 
                    className="w-full max-w-xs aspect-square object-cover rounded-lg relative z-10 shadow-md"
                  />
                </div>
              </div>
              <div className="md:w-2/3">
                <p className="text-lg md:text-xl text-gray-700 mb-8">
                  La vie est faite de moments. Certains prévisibles, d'autres imprévus, mais tous importants.
                  Avec Legatia, vous ne manquez plus jamais ces instants clés, même si vous ne pouvez pas y être physiquement présent.
                  Vous projetez votre présence, vos mots et votre soutien vers l'avenir, créant un fil émotionnel qui traverse le temps
                  et relie les générations.
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <ConceptItem 
                icon={<Mail className="h-5 w-5 text-legatia-orange" />} 
                text="Vos mots, préservés intacts, délivrés au moment parfait."
              />
              <ConceptItem 
                icon={<Gift className="h-5 w-5 text-legatia-orange" />} 
                text="Un geste qui grandit avec le temps pour un impact maximal."
              />
              <ConceptItem 
                icon={<Lock className="h-5 w-5 text-legatia-orange" />} 
                text="Une technologie au service de l'émotion et de la transmission."
              />
            </div>
            
            <p className="text-lg md:text-xl font-medium text-center">
              Ce n'est pas une simple capsule temporelle. C'est votre présence qui transcende le temps.
            </p>
          </div>
        </div>
        
        <div className="text-center">
          <Button className="btn-primary">Créer une capsule temporelle émotionnelle</Button>
        </div>
      </div>
    </section>
  );
};

export default NewConcept;
